/**
 * DeliveryDashboardPage — `/delivery` index landing for the operator console.
 *
 * Ported from `screens/src/pages/delivery/DeliveryDashboard.jsx`. KPI tiles
 * read live counts from `service-delivery` (routes, drivers, vehicles, POD
 * queue). The dispatcher-facing **interactive map UI** (Mapbox/Leaflet) is
 * deferred to Phase 4 mobile-driver, but the live GPS-positions list now
 * renders below the routes table via `GET /v1/gps/active-positions` — every
 * driver currently on an ACTIVE shift surfaces with their latest ping.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Gauge,
  Map as MapIcon,
  PackageCheck,
  Route as RouteIcon,
  Timer,
  Truck,
  Users,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  getDeliveryDailyDashboard,
  getDeliveryDashboard,
  listActiveDriverPositions,
  listDrivers,
  listPodQueue,
  listRoutes,
  listVehicles,
} from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const QUICK_LINKS = [
  { to: '/delivery/routes',      label: 'Routes',         icon: RouteIcon },
  { to: '/delivery/drivers',     label: 'Drivers',        icon: Users },
  { to: '/delivery/exceptions',  label: 'Exceptions',     icon: AlertTriangle },
  { to: '/delivery/performance', label: 'Performance',    icon: ClipboardCheck },
];

export function DeliveryDashboardPage(): JSX.Element {
  const dashboard = useQuery({
    queryKey: ['delivery', 'dashboard'],
    queryFn: () => getDeliveryDashboard(),
    retry: false,
    staleTime: 30_000,
  });

  // Daily KPI roll-up (deliveries today / out for delivery / failed today /
  // on-time rate / avg time per stop). Backed by GET /v1/delivery/dashboards
  // (plural) — distinct from the dispatcher-state view above.
  const daily = useQuery({
    queryKey: ['delivery', 'dashboards', 'daily'],
    queryFn: () => getDeliveryDailyDashboard(),
    retry: false,
    staleTime: 30_000,
  });

  const routes = useQuery({
    queryKey: ['delivery', 'routes', { pageSize: 1 }],
    queryFn: () => listRoutes({ pageSize: 1 }),
    retry: false,
    staleTime: 30_000,
  });
  const drivers = useQuery({
    queryKey: ['delivery', 'drivers', { pageSize: 1 }],
    queryFn: () => listDrivers({ pageSize: 1 }),
    retry: false,
    staleTime: 30_000,
  });
  const vehicles = useQuery({
    queryKey: ['delivery', 'vehicles', { pageSize: 1 }],
    queryFn: () => listVehicles({ pageSize: 1 }),
    retry: false,
    staleTime: 30_000,
  });
  const podQueue = useQuery({
    queryKey: ['delivery', 'pods', 'queue', { pageSize: 5 }],
    queryFn: () => listPodQueue({ pageSize: 5, reviewStatus: 'PENDING' }),
    retry: false,
    staleTime: 30_000,
  });

  function denied<T>(q: { isError: boolean; error: unknown; data: T | undefined }): boolean {
    return q.isError && q.error instanceof ApiError && (q.error.status === 401 || q.error.status === 403);
  }
  const routesDenied = denied(routes);
  const driversDenied = denied(drivers);
  const vehiclesDenied = denied(vehicles);
  const podDenied = denied(podQueue);
  const dashboardDenied = denied(dashboard);
  const dailyDenied = denied(daily);

  const recentRoutes = useQuery({
    queryKey: ['delivery', 'routes', 'recent'],
    queryFn: () => listRoutes({ pageSize: 8 }),
    retry: false,
    staleTime: 30_000,
  });

  const positions = useQuery({
    queryKey: ['delivery', 'gps', 'active-positions'],
    queryFn: () => listActiveDriverPositions(),
    retry: false,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const positionsDenied =
    positions.isError &&
    positions.error instanceof ApiError &&
    (positions.error.status === 401 || positions.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery & Logistics"
        description="Dispatch command centre — routes, drivers, vehicles, POD review."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {QUICK_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-nipost-green bg-nipost-green-light rounded-lg hover:bg-nipost-green hover:text-white transition-colors"
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </Link>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Routes"
          value={routes.isPending ? '—' : routesDenied ? '—' : String(routes.data?.total ?? 0)}
          hint={routesDenied ? 'Permission required' : 'All routes in operator scope'}
          icon={RouteIcon}
          tone={routesDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Drivers"
          value={drivers.isPending ? '—' : driversDenied ? '—' : String(drivers.data?.total ?? 0)}
          hint={driversDenied ? 'Permission required' : 'Drivers on the operator roster'}
          icon={Users}
          tone={driversDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Vehicles"
          value={vehicles.isPending ? '—' : vehiclesDenied ? '—' : String(vehicles.data?.total ?? 0)}
          hint={vehiclesDenied ? 'Permission required' : 'Vehicles in the operator fleet'}
          icon={Truck}
          tone={vehiclesDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="POD Review Queue"
          value={podQueue.isPending ? '—' : podDenied ? '—' : String(podQueue.data?.total ?? 0)}
          hint={podDenied ? 'Permission required' : 'PODs awaiting reviewer sign-off'}
          icon={CheckCircle2}
          tone={podDenied ? 'warning' : 'default'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard
          title="In-progress routes"
          value={dashboard.isPending ? '—' : dashboardDenied ? '—' : String(dashboard.data?.routesByStatus.IN_PROGRESS ?? 0)}
          hint={dashboardDenied ? 'Permission required' : 'Routes currently in progress'}
          tone={dashboardDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Failed stops today"
          value={dashboard.isPending ? '—' : dashboardDenied ? '—' : String(dashboard.data?.failedStopsToday ?? 0)}
          hint={dashboardDenied ? 'Permission required' : 'Stop FAIL transitions today'}
          icon={AlertTriangle}
          tone={dashboardDenied ? 'warning' : (dashboard.data?.failedStopsToday ?? 0) > 0 ? 'warning' : 'default'}
        />
        <KPICard
          title="Active driver shifts"
          value={dashboard.isPending ? '—' : dashboardDenied ? '—' : String(dashboard.data?.activeShifts ?? 0)}
          hint={dashboardDenied ? 'Permission required' : 'Drivers currently clocked in'}
          tone={dashboardDenied ? 'warning' : 'default'}
        />
      </div>

      {/* Daily KPI roll-up — backs the user-facing "today" tiles. */}
      <div data-testid="daily-kpi-row" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Deliveries today"
          value={daily.isPending ? '—' : dailyDenied ? '—' : String(daily.data?.deliveriesToday ?? 0)}
          hint={dailyDenied ? 'Permission required' : 'Stops completed today (UTC)'}
          icon={PackageCheck}
          tone={dailyDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Out for delivery"
          value={daily.isPending ? '—' : dailyDenied ? '—' : String(daily.data?.outForDelivery ?? 0)}
          hint={dailyDenied ? 'Permission required' : 'Pending stops on routes in progress'}
          icon={Truck}
          tone={dailyDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Failed today"
          value={daily.isPending ? '—' : dailyDenied ? '—' : String(daily.data?.failedToday ?? 0)}
          hint={dailyDenied ? 'Permission required' : 'Stop FAIL transitions today'}
          icon={AlertTriangle}
          tone={dailyDenied ? 'warning' : (daily.data?.failedToday ?? 0) > 0 ? 'warning' : 'default'}
        />
        <KPICard
          title="On-time rate"
          value={
            daily.isPending || dailyDenied
              ? '—'
              : `${daily.data?.onTimeRate ?? 0}%`
          }
          hint={dailyDenied ? 'Permission required' : 'Delivered ÷ attempted today'}
          icon={Gauge}
          tone={dailyDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Avg time / stop"
          value={
            daily.isPending || dailyDenied
              ? '—'
              : daily.data?.avgMinutesPerStop == null
                ? '—'
                : `${daily.data.avgMinutesPerStop} min`
          }
          hint={dailyDenied ? 'Permission required' : 'Mean arrived → completed today'}
          icon={Timer}
          tone={dailyDenied ? 'warning' : 'default'}
        />
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent routes</h2>
        {recentRoutes.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : recentRoutes.isError && recentRoutes.error instanceof ApiError && (recentRoutes.error.status === 401 || recentRoutes.error.status === 403) ? (
          <DeferredNotice title="Permission required">
            Your account does not have permission to view delivery routes. Please contact your administrator.
          </DeferredNotice>
        ) : recentRoutes.isError ? (
          <DeferredNotice title="Service unreachable">
            {(recentRoutes.error as Error).message}
          </DeferredNotice>
        ) : (recentRoutes.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-8 text-center">No routes yet.</p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Driver</th>
                  <th className="text-left px-4 py-2">Stops</th>
                  <th className="text-left px-4 py-2">Scheduled start</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentRoutes.data?.items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs font-bold text-gray-900">{r.routeCode}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-xs text-muted">{r.driverId ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-muted">{r.stopCount}</td>
                    <td className="px-4 py-2 text-xs text-muted">{new Date(r.scheduledStart).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/delivery/items/${encodeURIComponent(r.id)}`}
                        className="text-xs font-semibold text-nipost-green hover:underline"
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="active-positions-card">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Live driver positions</h3>
            <p className="text-xs text-muted mt-0.5">
              Driver GPS positions · refreshes every 30s
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-700" title="Map view coming soon">
            <Clock className="w-3.5 h-3.5" /> Map view coming soon
          </span>
        </div>
        {positions.isPending ? (
          <p className="px-5 py-6 text-sm text-muted">Loading driver positions…</p>
        ) : positionsDenied ? (
          <DeferredNotice title="Permission required">
            <code className="font-mono">Delivery/readGps</code> is missing from the dev token.
          </DeferredNotice>
        ) : positions.isError ? (
          <DeferredNotice title="Service unreachable">
            {(positions.error as Error).message}
          </DeferredNotice>
        ) : (positions.data?.items.length ?? 0) === 0 ? (
          <div className="px-5 py-10 text-center">
            <MapIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-muted">No active drivers reporting GPS right now.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="active-positions-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Driver</th>
                  <th className="text-left px-4 py-2">Shift</th>
                  <th className="text-left px-4 py-2">Lat / Lng</th>
                  <th className="text-left px-4 py-2">Speed</th>
                  <th className="text-left px-4 py-2">Reported</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {positions.data?.items.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-900">{p.driverId}</td>
                    <td className="px-4 py-2 font-mono text-[10px] text-muted">{p.shiftId.slice(0, 8)}…</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">
                      {p.latitude}, {p.longitude}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {p.speedKph !== null ? `${p.speedKph} kph` : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {new Date(p.recordedAt).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
